import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";

// ============================================
// Types
// ============================================

export interface PageVisibilityContextValue {
  /** Whether the page/tab is currently visible (Page Visibility API) */
  isVisible: boolean;
  /** Whether the browser window currently has focus */
  isFocused: boolean;
  /** 
   * Whether the app is actively being used.
   * True only when the tab is visible AND the window has focus.
   * Use this for most use cases like pausing polling.
   */
  isActive: boolean;
  /** The raw visibilityState from the document */
  visibilityState: DocumentVisibilityState;
}

export interface PageVisibilityProviderProps {
  children: React.ReactNode;
}

// ============================================
// Context
// ============================================

const PageVisibilityContext =
  createContext<PageVisibilityContextValue | null>(null);

// ============================================
// Hook
// ============================================

export const usePageVisibilityContext = (): PageVisibilityContextValue => {
  const context = useContext(PageVisibilityContext);
  if (!context) {
    throw new Error(
      "usePageVisibilityContext must be used within a PageVisibilityProvider"
    );
  }
  return context;
};

// ============================================
// Helpers to get current state
// ============================================

const getVisibilityState = (): DocumentVisibilityState => {
  if (typeof document === "undefined") {
    return "visible";
  }
  return document.visibilityState;
};

const getIsFocused = (): boolean => {
  if (typeof document === "undefined") {
    return true;
  }
  return document.hasFocus();
};

// ============================================
// Provider
// ============================================

export const PageVisibilityProvider: React.FC<PageVisibilityProviderProps> = ({
  children,
}) => {
  const [visibilityState, setVisibilityState] = useState<DocumentVisibilityState>(
    getVisibilityState
  );
  const [isFocused, setIsFocused] = useState<boolean>(getIsFocused);

  // ============================================
  // Visibility change handler
  // ============================================

  const handleVisibilityChange = useCallback(() => {
    setVisibilityState(getVisibilityState());
  }, []);

  // ============================================
  // Focus/blur handlers
  // ============================================

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  // ============================================
  // Set up event listeners
  // ============================================

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, [handleVisibilityChange, handleFocus, handleBlur]);

  // ============================================
  // Context value
  // ============================================

  const isVisible = visibilityState === "visible";
  const isActive = isVisible && isFocused;

  const value = useMemo<PageVisibilityContextValue>(
    () => ({
      isVisible,
      isFocused,
      isActive,
      visibilityState,
    }),
    [isVisible, isFocused, isActive, visibilityState]
  );

  return (
    <PageVisibilityContext.Provider value={value}>
      {children}
    </PageVisibilityContext.Provider>
  );
};